import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import { ArrowLeft, CircleCheck, Circle, AlertTriangle } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";

const CONFIRMATION_OPTIONS = [
  { key: "auto", titleKey: "AutoConfirmTitle", descKey: "AutoConfirmDesc", icon: "/icons/auto.svg" },
  { key: "manual", titleKey: "ManualConfirmTitle", descKey: "ManualConfirmDesc", icon: "/icons/manual.svg" },
];

const HotelAdd_YSubmit = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:850px)");
  const { hotelId, error: hotelError, submit, loading } = useHotel();

  const [confirmationMethod, setConfirmationMethod] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const isNextDisabled = !confirmationMethod || isSubmitting;

  useEffect(() => {
    if (!hotelId) {
      navigate("/hotel/add/info1");
    }
  }, [hotelId, navigate]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      await submit(confirmationMethod);
      setShowSuccessModal(true);
    } catch (err) {
      setSubmitError(err.message || "Failed to submit hotel");
      console.error("Submit error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Button
              onClick={() => navigate(-1)}
              sx={{
                minWidth: "auto",
                width: isMobile ? "36px" : "44px",
                height: isMobile ? "36px" : "44px",
                borderRadius: "50%",
                backgroundColor: "#31BACD",
                color: "#fff",
                padding: 0,
                "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
              }}
            >
              <ArrowLeft size={isMobile ? 18 : 22} />
            </Button>
            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
              {t('LastStepLabel')}
            </Typography>
          </Box>

          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "18px" : "20px", color: "#000", mb: 1 }}>
            {t('SelectConfirmationMethodTitle')}
          </Typography>
          <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#666", mb: 3 }}>
            {t('SelectConfirmationMethodDesc')}
          </Typography>

          {(submitError || hotelError) && (
            <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
              {submitError || hotelError}
            </Alert>
          )}

          {confirmationMethod === "auto" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                backgroundColor: "#FFF8E1",
                border: "1px solid #FFE082",
                borderRadius: "10px",
                padding: isMobile ? "12px 16px" : "14px 20px",
                mb: 2,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <AlertTriangle size={18} color="#F9A825" style={{ flexShrink: 0, marginTop: 2 }} />
              <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#8D6E00" }}>
                {t('AutoConfirmWarning')}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
            {CONFIRMATION_OPTIONS.map((option) => {
              const isSelected = confirmationMethod === option.key;
              return (
                <Box
                  key={option.key}
                  onClick={() => setConfirmationMethod(option.key)}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    backgroundColor: "#FFFFFF",
                    border: isSelected ? "1.5px solid #31BACD" : "1.5px solid #D1D1D1",
                    borderRadius: "10px",
                    padding: isMobile ? "16px" : "20px 24px",
                    cursor: "pointer",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  {isSelected ? (
                    <CircleCheck size={20} color="#31BACD" style={{ flexShrink: 0, marginTop: 2 }} />
                  ) : (
                    <Circle size={20} color="#D1D1D1" style={{ flexShrink: 0, marginTop: 2 }} />
                  )}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                        {t(option.titleKey)}
                      </Typography>
                      <Box component="img" src={option.icon} alt="" sx={{ width: 20, height: 20 }} />
                    </Box>
                    <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#666" }}>
                      {t(option.descKey)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={() => navigate("/hotel/onboarding")}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled}
                onClick={handleSubmit}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {isSubmitting ? <CircularProgress size={20} color="inherit" /> : t('Submit')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />

      {showSuccessModal && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 2 : 4,
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: isMobile ? "24px" : "32px 40px",
              width: isMobile ? "90%" : "auto",
              maxWidth: "700px",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Box sx={{ width: isMobile ? "100%" : "320px", flexShrink: 0 }}>
              <Box
                component="img"
                src="/icons/ppclogo.png"
                alt="Pet Pet Club"
                sx={{ height: isMobile ? "20px" : "24px", mb: 2 }}
              />
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Box component="img" src="/icons/success.svg" alt="" sx={{ width: 72, height: 72 }} />
              </Box>
              <Typography sx={{ fontSize: isMobile ? "11px" : "12px", color: "#666", mb: 3 }}>
                {t('SubmitSuccessDesc')}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/hotel/onboarding")}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "13px" : "14px",
                  padding: "8px 0",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                }}
              >
                {t('BackToHomepage')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default HotelAdd_YSubmit;
